/* eslint-disable no-implicit-coercion */
/* eslint-disable import/no-unresolved */
import { useEffect, useMemo } from "react";
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  Flex,
  FormControl,
  SimpleGrid,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../../components/breadCrumb";
import Loader from "../../../components/PageLoader";
import ReactHookFormDropdown from "../../../components/forms/react-hook-dropdown";
import FormInput from "../../../components/forms/form-Input";
import NumericInput from "../../../components/forms/react-hook-numeric-input";
import {
  NOTIFICATION_TYPES,
  OLA_NOTIFICATION_TYPES,
  EVENT_CATEGORIES,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_TRIGGER,
  ESCALATION_LEVELS,
  SLA_ASSIGNMENT_GROUPS,
  OLA_ASSIGNMENT_GROUPS,
} from "../constants";

import {
  useLookupCustomers,
  useLookupAssignmentGroup,
  useLookupRegions,
} from "../../../services/lookups-mutations";

import { useAddNotificationRule } from "../../../services/notifications";
import { Paths } from "../../../routes/paths";
import { ACTIVITY_TYPES } from "pages/customers/constants";
import ReactHookFormMultiSelect from "components/forms/react-hook-multiselect";

const OLA_NEW_TICKET_ACTIVITY_TYPES = [
  { id: "Customer Service", text: "Customer Service" },
  { id: "ETISALAT Third Party", text: "ETISALAT Third Party" },
  { id: "Facilities Request", text: "Facilities Request" },
  { id: "TGM Remote Support", text: "TGM Remote Support" },
  { id: "Unusual Event", text: "Unusual Event" },
  { id: "Customer Complaints", text: "Customer Complaints" },
];

const OLA_OTHER_ACTIVITY_TYPES = [
  { id: "Customer Service", text: "Customer Service" },
  { id: "ETISALAT Third Party", text: "ETISALAT Third Party" },
  { id: "Facilities Request", text: "Facilities Request" },
  { id: "TGM Remote Support", text: "TGM Remote Support" },
  { id: "Unusual Event", text: "Unusual Event" },
  { id: "Customer Complaints", text: "Customer Complaints" },
  { id: "Voice Of Staff", text: "Voice Of Staff" },
  { id: "Incident - Standard", text: "Incident - Standard" },
  { id: "Incident - Emergency", text: "Incident - Emergency" },
  { id: "Panic Alert Incident", text: "Panic Alert Incident" },
  { id: "Marketing", text: "Marketing" },
  { id: "Missed And Delayed Services", text: "Missed And Delayed Services" },
  { id: "TGSI Helpdesk", text: "TGSI Helpdesk" },
];

export default function AddNotification() {
  const navigate = useNavigate();

  /* ---------------- LOOKUPS ---------------- */
  const {
    mutate: getCustomers,
    data: customersList,
    isPending,
  } = useLookupCustomers();

  const { mutate: getAssignmentGroup } = useLookupAssignmentGroup();

  const { mutate: getRegions, data: regionsList } = useLookupRegions();

  const {
    mutate: addNotification,
    isPending: saving,
    isSuccess,
  } = useAddNotificationRule();

  useEffect(() => {
    getCustomers();
    getAssignmentGroup();
    getRegions("UAE");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSuccess) {
      navigate(Paths.notification);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  /* ---------------- HELPERS ---------------- */

  const normalizedRegions = useMemo(
    () =>
      [{ id: "All", name: "All" }, ...(regionsList?.data || [])].map((r) => ({
        id: String(r.id ?? r.name ?? ""),
        name: String(r.name ?? ""),
      })),
    [regionsList],
  );

  const prependAllOption = (list, key = "id", textKey = "name") => {
    if (!list) return [];
    return [{ [key]: "All", [textKey]: "All" }, ...list];
  };

  const handleAllSelection = (values = [], allKey = "id") => {
    if (!Array.isArray(values)) return [];

    const hasAll = values.some((v) => v?.[allKey] === "All");

    if (!hasAll) return values;

    // If All selected with others → keep only All
    if (values.length > 1) {
      return values.filter((v) => v?.[allKey] !== "All");
    }

    return values;
  };

  /* ---------------- FORM ---------------- */

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      isActive: true,
      notificationChannels: [],
      escalationLevel: [],
      activities: [],
      customers: [],
      regions: [],
      whenSent: null,
      assignmentGroup: null,
      notificationType: null,
      eventCategory: null,
      description: "",
      threshold: "",
    },
  });

  const selectedNotificationType = watch("notificationType");

  const selectedEventCategory = watch("eventCategory");
  const isOLA = selectedEventCategory === "OLA";

  useEffect(() => {
    // Clear dependent fields when Type changes
    setValue("notificationType", null);
    setValue("whenSent", null);
  }, [selectedEventCategory, setValue]);

  useEffect(() => {
    if (selectedNotificationType || !selectedNotificationType) {
      setValue("threshold", 0);
      setValue("activities", []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOLA, selectedNotificationType]);

  /* ---------------- VALIDATION ---------------- */
  const formValidationRules = {
    description: { required: "Please Enter Description" },
    eventCategory: { required: "Please Select Event Category" },
    notificationType: { required: "Please Select Notification Type" },
    threshold: { required: "Please Enter Threshold" },
    escalationLevel: {
      validate: (v) => v?.length > 0 || "Please Select Escalation Level",
    },
    whenSent: {
      validate: (v) => !isOLA || !!v || "When Sent is required",
    },
    assignmentGroup: { required: "Please Select Assignment Group" },
    activities: { validate: (v) => v?.length > 0 || "Please Select Activity" },
    customers: { validate: (v) => v?.length > 0 || "Please Select Customer" },
    regions: { validate: (v) => v?.length > 0 || "Please Select Region(s)" },
    notificationChannels: {
      validate: (v) => v?.length > 0 || "Please Select Notification Channels",
    },
  };

  /* ---------------- SAVE ---------------- */
  const onSave = (data) => {
    const isPercentageElapsed = ["AboutToBreach", "Breach"].includes(
      data.notificationType,
    );

    const payload = {
      description: data.description,
      eventCategory: data.eventCategory,
      threshold: Number(data.threshold),

      thresholdType: isPercentageElapsed
        ? "PercentageElapsed"
        : "MinutesAfterBreach",

      notificationType: data.notificationType,
      escalationLevel: data.escalationLevel.map((e) => e.id),
      assignmentGroup: data.assignmentGroup,
      regions: data.regions.map((r) => r.id),
      activities: data.activities.map((a) => a.value ?? a.id), // handle both original and "All" option
      customers: data.customers.map((c) => c.customerId),
      isActive: data.isActive,
      whenSent: isOLA ? data.whenSent : null,
    };

    //     if (data.whenSent) payload.whenSent = data.whenSent.id;

    if (data.notificationChannels?.length) {
      payload.notificationChannels = data.notificationChannels.map((c) => c.id);
    }

    addNotification(payload);
  };

  const activityTypeOptions = (() => {
    // Non-OLA → use original list
    if (!isOLA) return ACTIVITY_TYPES;

    // OLA + New Ticket Created
    if (selectedNotificationType === "NewTicketCreated") {
      return OLA_NEW_TICKET_ACTIVITY_TYPES;
    }
    // OLA + any other notification type
    return OLA_OTHER_ACTIVITY_TYPES;
  })();

  return (
    <>
      {isPending || saving ? <Loader /> : null}

      <div style={{ maxWidth: "100%", margin: "0 auto", padding: "20px" }}>
        <BreadCrumb
          links={[
            { text: "Notification Rules", link: Paths.notification },
            { text: "Add Notification Rule", isCurrentPage: true },
          ]}
        />

        <Card>
          <CardBody>
            <form onSubmit={handleSubmit(onSave)}>
              <dic className="flex justify-between w-full mb-3">
                <h2 className="font-bold text-lg">Add Notification Rule</h2>

                <FormControl className="max-w-max w-full">
                  <Controller
                    control={control}
                    name="isActive"
                    render={({ field }) => (
                      <Checkbox
                        isChecked={field.value}
                        onChange={field.onChange}
                      >
                        Active
                      </Checkbox>
                    )}
                  />
                </FormControl>
              </dic>

              {/* 4-column grid */}
              <SimpleGrid columns={{ base: 1, md: 3, lg: 4 }} spacing="20px">
                <ReactHookFormDropdown
                  control={control}
                  errors={errors}
                  isRequired
                  filterable
                  label="Type"
                  name="eventCategory"
                  id="id"
                  text="text"
                  data={EVENT_CATEGORIES}
                  placeholder="Select a Value"
                  rules={formValidationRules.eventCategory}
                />

                <FormInput
                  control={control}
                  errors={errors}
                  isRequired
                  label="Description"
                  name="description"
                  rules={formValidationRules.description}
                />

                <ReactHookFormDropdown
                  control={control}
                  errors={errors}
                  isRequired
                  filterable
                  label="Notification Type"
                  name="notificationType"
                  data={isOLA ? OLA_NOTIFICATION_TYPES : NOTIFICATION_TYPES}
                  placeholder="Select a Value"
                  rules={formValidationRules.notificationType}
                />

                <NumericInput
                  control={control}
                  errors={errors}
                  isRequired
                  label={`Threshold (${selectedNotificationType === "BreachEscalation" ? "min" : "%"})`}
                  name="threshold"
                  rules={formValidationRules.threshold}
                  inputProps={
                    selectedNotificationType !== "BreachEscalation"
                      ? {
                          min: 1,
                          max: 100,
                        }
                      : {
                          max: 500,
                        }
                  }
                />
                {isOLA ? (
                  <ReactHookFormDropdown
                    control={control}
                    errors={errors}
                    isRequired
                    filterable
                    label="When Sent"
                    name="whenSent"
                    id="id"
                    text="text"
                    data={NOTIFICATION_TRIGGER.map((item) => ({
                      value: item.value,
                      label: item.label,
                    }))}
                    placeholder="Select a Value"
                    rules={formValidationRules.whenSent}
                  />
                ) : null}

                <ReactHookFormMultiSelect
                  label="Escalation Level"
                  name="escalationLevel"
                  control={control}
                  errors={errors}
                  isRequired
                  filterable
                  placeholder="Select a Value"
                  data={ESCALATION_LEVELS}
                  id="id"
                  text="text"
                  rules={formValidationRules.escalationLevel}
                />

                <ReactHookFormDropdown
                  control={control}
                  errors={errors}
                  isRequired
                  filterable
                  label="Assignment Group"
                  name="assignmentGroup"
                  data={(isOLA
                    ? OLA_ASSIGNMENT_GROUPS
                    : SLA_ASSIGNMENT_GROUPS
                  ).map((item) => ({
                    value: item.name,
                    label: item.name,
                    name: item.name,
                  }))}
                  placeholder="Select a Value"
                  rules={formValidationRules.assignmentGroup}
                />

                <ReactHookFormMultiSelect
                  label="Activity Types"
                  name="activities"
                  control={control}
                  errors={errors}
                  isRequired
                  filterable
                  placeholder="Select a Value"
                  data={prependAllOption(activityTypeOptions, "value", "label")}
                  onChange={(value) => handleAllSelection(value, "value")}
                  id="value"
                  text="label"
                  rules={formValidationRules.activities}
                />

                <ReactHookFormMultiSelect
                  label="Customers"
                  name="customers"
                  control={control}
                  errors={errors}
                  isRequired
                  filterable
                  placeholder="Select a Value"
                  data={prependAllOption(
                    customersList?.data,
                    "customerId",
                    "name",
                  )}
                  id="customerId"
                  text="name"
                  onChange={(value) => handleAllSelection(value, "id")}
                  rules={formValidationRules.customers}
                />

                <ReactHookFormMultiSelect
                  label="Regions"
                  name="regions"
                  dataItemKey="id"
                  textField="text"
                  control={control}
                  errors={errors}
                  isRequired
                  placeholder="Select a Value"
                  data={normalizedRegions}
                  id="id"
                  text="name"
                  onChange={(value) => handleAllSelection(value, "id")}
                  rules={formValidationRules.regions}
                />

                <ReactHookFormMultiSelect
                  label="Notification Channels"
                  name="notificationChannels"
                  control={control}
                  errors={errors}
                  isRequired
                  placeholder="Select a Value"
                  data={NOTIFICATION_CHANNELS}
                  id="id"
                  text="text"
                  rules={formValidationRules.notificationChannels}
                />
              </SimpleGrid>

              <Flex justify="flex-end" mt="30px" gap="10px">
                <Button
                  variant="outline"
                  onClick={() => navigate(Paths.notification)}
                >
                  Cancel
                </Button>
                <Button type="submit">Add Notification Rule</Button>
              </Flex>
            </form>
          </CardBody>
        </Card>
      </div>
    </>
  );
}
